<template>
    <div class="pipeline-tile">
        <div :class="`inner ${pipeline.details.status}`">
            <div v-if="pipeline.hint" class="hint">
                <a v-if=pipeline.hint_url :href=pipeline.hint_url :target=pipeline.details.id class="user">
                    {{ pipeline.hint }}
                </a>
                <span v-if=!pipeline.hint_url>
                    {{ pipeline.hint }}
                </span>
            </div>
            <div class="name">
                <a :href=pipeline.details.web_url :target=pipeline.details.id class="user">
                    <span v-if="show_namespace">
                      {{ pipeline.namespace }}/{{ name }}
                    </span>
                   <span v-else-if="show_namespace_ommitted_group">
                      {{ pipeline.namespace.replace(/^[^/]+\/?/,"") }}/{{ name }}
                    </span>
                    <span v-else>
                        {{ name }}
                    </span>
                </a>
            </div>
            <div class="duration">{{ pipelineDuration(pipeline.details) }}</div>
            <div class="bottom">
                <div class="started">{{ formatAge(pipeline.details.started_at) }}</div>
                <div class="branch">{{ pipeline.details.ref }}</div>
                <div class="user">{{ `#${pipeline.details.id}` }}</div>
            </div>
            <div class="history" v-if="_show_history">
                <a :href=pipeline_history_link :target=pipeline_history_link>history: {{ pipeline_history }}</a>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {GitLabPipelineDetails, Pipeline} from '@/interfaces'
import moment from 'moment'

import { defineComponent } from 'vue';

export default defineComponent({
  name: 'PipelineTile',
    props: {
        name: String,
        pipeline: Object as () => Pipeline,
        show_namespace: Boolean,
        show_namespace_ommitted_group: Boolean,
        show_branches: Boolean,
        show_history: Boolean
    },
    methods: {
        pipelineDuration(pipeline: GitLabPipelineDetails) {
            if (pipeline.status === 'running') {
                const now = new Date()
                const started = new Date(pipeline.started_at)
                const time = now.getTime() - started.getTime()
                return this.formatDuration(Math.floor(time / 1000))
            } else {
                return this.formatDuration(pipeline.duration)
            }
        },
        formatAge(dateStr: string) {
            const fromNow = moment(dateStr).fromNow()
            return fromNow.includes('seconds') ? 'Just now' : fromNow
        },
        formatDuration(time: number) {
            const minutes = Math.floor(time / 60)
            const seconds = time - minutes * 60
            const minutesStr = minutes > 1 ? 'minutes' : 'minute'
            const secondStr = seconds > 1 ? 'seconds' : 'second'
            return minutes > 0 ?
                `${minutes} ${minutesStr} ${seconds} ${secondStr}` :
                `${seconds} ${secondStr}`
        },
    },
    computed: {
        _show_history(): boolean {
            const details = this.pipeline?.details || {
                status: ""
            }
            return this.show_history && details.status == 'failed'
        },
        pipeline_history(): string {
            const pipelines = this.pipeline?.history || [];

            return pipelines.map(p => {
                // see statuses
                // https://docs.gitlab.com/ee/api/pipelines.html
                switch (p.status) {
                    case 'canceled':
                        return '꩷'; // https://www.compart.com/en/unicode/U+AA77
                    case 'failed':
                        return '✖';
                    case 'manual':
                        return '🦵';
                    case 'skipped':
                        return '-';
                    case 'success':
                        return '✓';
                    default:
                        return p.status
                }
            }).join(' ')
        },
        pipeline_history_link(): string {
            const details = this.pipeline?.details || {
                web_url: "",
                ref: "",
                id: 0,
                status: "",
                started_at: "",
              source: "",
            };
            const parts = details.web_url.split("/")
            parts.pop() // omit project id
            const base_link = parts.join("/") + "?scope=all&ref=" + details.ref;
            if ( details.source =="schedule"){
                return base_link + "&source=schedule"
            }
            return base_link;
        }
    }
})
</script>

<style lang="scss">

$red: #dc4644;
$green: rgba(57, 158, 96, 0.8);
$grey: #475158;
$orange: #fc9403;
$blue-dark: rgba(51, 109, 255, 1);
$blue: rgba(51, 109, 255, 0.8);
$blue-light: rgba(51, 109, 255, 0.5);
$main: #efefef;

:root {
  color: #ffffff;
}

.inner {
  width: 100%;
  height: 100%;
  position: relative;
  background-color: $grey;
  box-shadow: 0 0.25rem 1rem rgba(48, 55, 66, .15);
  padding: 0.5rem 0.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: background-color 1s ease-in-out;

  &.success {
    background-color: $green;
  }

  &.running {
    background-color: $blue;

    animation-name: running;
    animation-duration: 1s;
    animation-timing-function: ease-out;
    animation-direction: alternate;
    animation-iteration-count: infinite;
    animation-play-state: running;

    @keyframes running {
      0% {
        background-color: $blue-light;
        color: black;
      }

      50% {
        background-color: $blue;
        color: yellow;
      }

      100% {
        background-color: $blue-dark;
      }
    }
  }

  &.failed {
    background-color: $red;
  }

  &.pending {
    background-color: $orange;
  }

  .name a {
    color: $main
  }
}

.status {
  line-height: 1;
  font-weight: 400;
  text-transform: capitalize;
  margin-bottom: 0.5rem;
}

.hint {
  font-size: 0.7em;
  opacity: 0.7;

  a {
    color: $main
  }
}

.history {
  a {
    color: $main
  }
}

.name {
  font-size: 1em;
  font-weight: 700;
  line-height: 1;
  color: $main span {
    display: inline-flex;
  }
}

.duration {
  font-size: 0.7em;
}

.branches {
  font-size: 0.7em;
  width: 100%;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.bottom {
  width: 100%;
  padding: 0.5rem 0.5rem 0;
  margin-top: auto;
  text-transform: uppercase;
  font-size: 0.6em;
  color: #efefef;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-self: flex-end;
  justify-content: space-between;

  .branch {
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 60%;
    overflow: hidden;
  }
}

.history {
  color: #efefef;
  font-size: 0.6em;
  width: 100%;
  padding: 0 0.5rem 0.5rem;
  text-align: left;
  text-transform: uppercase;
}

</style>
